import { LitElement, PropertyValueMap, css, html, unsafeCSS } from 'lit';
import { customElement, property, query, state } from 'lit/decorators';

import { Dialog } from '@material/mwc-dialog';
import { delay } from '../../helpers/utils';
import { detectMobileViewport } from '../../helpers/device';
import { heights } from '../../style/variables';
import topicData from '../../helpers/state/topic-data';

@customElement('setting-drawer')
export class SettingDrawer extends LitElement {

    @query('#modalDialog')
    modalDialogComponent!: Dialog;

    @property({ type: Boolean })
    openInitially: boolean = false;

    @property({ type: String })
    title: string = '';

    @property({ type: Boolean })
    adminPortalMobile: boolean = false;

    @property({ type: Boolean })
    commandCenter: boolean = false;

    @property({ type: Boolean })
    noHeader: boolean = false;

    @property({ type: String })
    coverImage: string = '';

    @property({ type: String })
    coverImageMaxHeight: string = '180px';

    @property({ type: String })
    coverImageLogo: string = '';

    @property({ type: Boolean })
    coverImageLogoBackground: boolean = true;

    // When true and isExpanded is true, the drawer opens at the full
    // window height (matching what toggleExpand produces). Off by default
    // so existing callers keep the historical 0.9 × windowHeight behavior.
    @property({ type: Boolean })
    fullHeightOnExpand: boolean = false;

    @state()
    additionalHeightReduction: string = '0px';

    @state()
    isOpened: boolean = false;

    @state()
    isExpanded: boolean = false;

    // we have to use this because heights on mobile are all different based on the browser type
    @state()
    windowHeight: number = window.innerHeight;

    @state()
    highZIndex: boolean = false;

    private triggerElement?: HTMLElement;

    private isClosing: boolean = false;

    disconnectedCallback(): void {
        super.disconnectedCallback();
        document.removeEventListener('keydown', this.handleKeydown);
    }

    private readonly handleKeydown = (e: KeyboardEvent) => {
        if (e.key === 'Escape' && this.isOpened) {
            void this.close();
        }
    };

    async firstUpdated(changedProperties: PropertyValueMap<any> | Map<PropertyKey, unknown>) {
        if (this.openInitially) {
            await this.open();
        }

        if (this.noHeader) {
            this.isExpanded = true;
        }
    }

    async close() {
        if (!this.isOpened || this.isClosing) {
            return;
        }
        this.isClosing = true;
        if (!detectMobileViewport()) {
            this.modalDialogComponent?.close();
        }
        this.style.setProperty('--slotted-content-height', `0px`);
        this.style.setProperty('--drawer-transform', 'translateY(100%)');
        this.requestUpdate();
        await delay(300);

        this.isOpened = false;
        this.isClosing = false;
        document.removeEventListener('keydown', this.handleKeydown);
        this.dispatchEvent(new CustomEvent('on-close'));

        // Return focus to the element that triggered the drawer
        if (this.triggerElement) {
            this.triggerElement.focus();
            this.triggerElement = undefined;
        }
    }

    get coverImageHeight(): string {
        return this.coverImage ? this.coverImageMaxHeight : '0px';
    }

    // these heights are set dynamically because the needed height of the content is different on different pages. E.g. admin portal mobile is tiny.

    async open() {
        if (this.isOpened) {
            return;
        }

        // Remember the element that opened the drawer so we can return focus
        this.triggerElement = (this.getRootNode() as ShadowRoot | Document).activeElement as HTMLElement ?? undefined;

        this.style.setProperty('--slotted-content-height', `0px`);
        this.style.setProperty('--drawer-transform', this.isOpened ? 'translateY(0)' : 'translateY(100%)');
        this.requestUpdate();

        await delay(100);

        this.isOpened = true;
        if (this.adminPortalMobile) {
            this.additionalHeightReduction = '250px';
        } else if (this.fullHeightOnExpand) {
            // Sign-up / form drawers leave just a small map peek above
            // the drawer instead of the standard mapHeader strip.
            this.additionalHeightReduction = '16px';
        } else {
            // assume it is command center
            this.additionalHeightReduction = heights.mapHeader;
        }
        // if the --bottom-height is 0, set the z-index on setting-drawer__wrapper to 20
        this.highZIndex = true;

        // Default: expanded = 0.9 × windowHeight, collapsed = 0.6 × windowHeight.
        // Callers can opt into 1.0 × windowHeight (matching toggleExpand) via
        // fullHeightOnExpand — used by the embedded check-in sign-up flow.
        const expandedMultiplier = this.fullHeightOnExpand ? 1 : 0.9;
        this.isExpanded ?
            this.style.setProperty('--slotted-content-height', `calc(${this.windowHeight * expandedMultiplier}px - ${heights.nav} - var(--bottom-height) - ${this.additionalHeightReduction} - ${this.coverImageHeight})`) :
            this.style.setProperty('--slotted-content-height', `calc(${this.windowHeight * .6}px - ${heights.nav} - var(--bottom-height) - ${this.additionalHeightReduction} - ${this.coverImageHeight})`);

        this.style.setProperty('--drawer-transform', this.isOpened ? 'translateY(0)' : 'translateY(100%)');

        if (!detectMobileViewport()) {
            this.modalDialogComponent.open = true;
        }

        document.addEventListener('keydown', this.handleKeydown);

        // Move focus into the drawer after it opens
        await delay(100);
        const closeBtn = this.shadowRoot?.querySelector('.drawer-close-btn, .no-header-close') as HTMLElement;
        closeBtn?.focus();
    }

    toggleExpand() {
        this.isExpanded = !this.isExpanded;
        this.style.setProperty('--slotted-content-height', this.isExpanded ? `calc(${this.windowHeight}px - ${heights.nav} - var(--bottom-height) - ${this.additionalHeightReduction} - ${this.coverImageHeight})` : `calc(65vh - ${heights.nav} - var(--bottom-height) - ${this.additionalHeightReduction} - ${this.coverImageHeight})`);
        this.requestUpdate();
    }

    render() {
        const isMobilePreview = topicData.mapComponent ? topicData.mapComponent.mobilePreview : false;
        if (!detectMobileViewport(isMobilePreview)) {
            return html`
                <!-- @closed: the dialog closes itself on scrim-click/Escape, which would otherwise leave isOpened latched true -->
                <mwc-dialog id="modalDialog" class="dialog" hideActions @closed="${async () => await this.close()}">
                    <modal-dialog  size="sm" @close="${async () => await this.close()}">
                        <slot slot="body"></slot>
                    </modal-dialog>
                </mwc-dialog>
            `;
        }
        return html`
            <div class="setting-drawer__wrapper ${this.highZIndex ? 'highzindex' : ''}" style="visibility: ${this.isOpened ? 'visible' : 'hidden'};">
                <div class="setting-drawer ${this.isExpanded ? 'setting-drawer--expanded' : ''}" role="dialog" aria-modal="true" aria-label="${this.title || 'Details'}">
                    ${this.coverImage ? html`
                        <div class="cover-image-container" style="height:${this.coverImageMaxHeight}; max-height:${this.coverImageMaxHeight}">
                            <img src="${this.coverImage}" class="cover-image" alt="" />
                            ${this.coverImageLogo ? html`
                                <div class="cover-image-logo-overlay">
                                    <img src="${this.coverImageLogo}" alt="Logo" style="${this.coverImageLogoBackground ? 'background-color: white;' : 'background-color: transparent; box-shadow: none;'}" />
                                </div>
                            ` : ''}
                            <div class="cover-image-controls">
                                <button class="drawer-icon-btn" aria-label="${this.isExpanded ? 'Collapse drawer' : 'Expand drawer'}" @click="${this.toggleExpand}">
                                    <mwc-icon class="cover-image-icon" aria-hidden="true">${this.isExpanded ? 'keyboard_arrow_down' : 'keyboard_arrow_up'}</mwc-icon>
                                </button>
                                <button class="drawer-icon-btn drawer-close-btn" aria-label="Close" @click="${async () => await this.close()}">
                                    <mwc-icon class="cover-image-icon" aria-hidden="true">close</mwc-icon>
                                </button>
                            </div>
                        </div>
                    ` : this.noHeader ? '' : html`
                        <div class="setting-drawer__controls" style="visibility: ${this.isOpened ? 'visible' : 'hidden'};">
                            <button class="drawer-icon-btn" aria-label="${this.isExpanded ? 'Collapse drawer' : 'Expand drawer'}" @click="${this.toggleExpand}">
                                <mwc-icon aria-hidden="true">${this.isExpanded ? 'keyboard_arrow_down' : 'keyboard_arrow_up'}</mwc-icon>
                            </button>
                            <h2>${this.title}</h2>
                            <button class="drawer-icon-btn drawer-close-btn" aria-label="Close" @click="${async () => await this.close()}">
                                <mwc-icon aria-hidden="true">close</mwc-icon>
                            </button>
                        </div>
                    `}
                    <div class="setting-drawer__content" style="visibility: ${this.isOpened ? 'visible' : 'hidden'};">
                        ${this.noHeader && !this.coverImage ? html`<button class="drawer-icon-btn no-header-close drawer-close-btn" aria-label="Close" @click="${async () => await this.close()}"><mwc-icon aria-hidden="true">close</mwc-icon></button>` : ''}
                        <slot></slot>
                    </div>
                </div>
            </div>
        `;
    }

    static get styles() {
        return css`
            :host {
                --bottom-height: ${unsafeCSS(heights.mobileSettingsControls)};
                --bottom-height-half: ${unsafeCSS(heights.mobileSettingsControlsHalf)};
                --drawer-bg-color: white;
                --drawer-border-color: rgb(236, 236, 236);
                --slotted-content-height: 50vh; /* default height */
                transition: height 0.3s ease-in-out;
            }

            @media (max-width: 768px) {
                ::slotted(*) {
                    height: var(--slotted-content-height);
                    display: flex;
                    flex-direction: column;
                    transition: height 0.3s ease-in-out;
                }
            }

            .setting-drawer__wrapper {
                height: 100%;
                width: 100%;
                z-index: 4;
                pointer-events: none;
                position: absolute;
                top: 0;
                left: 0;
            }

            .highzindex {
                z-index: 20;
            }

            /* Headerless drawers slot full-bleed content (e.g. the
               challenge point card's image carousel) directly under
               this button. Make it a proper circular target: a solid
               disc with a shadow reads over any photo, the 40px size +
               raised z-index keeps taps from falling through to the
               carousel's fullscreen image link beneath it. */
            /* Element+class selector (specificity 0,0,1,1) so the white
               disc + dark glyph beat the later, equal-weight
               .drawer-icon-btn rule (single class) which otherwise
               resets background to none / color to inherit and leaves a
               see-through X over the carousel. */
            button.no-header-close {
                position: absolute;
                top: 12px;
                right: 12px;
                z-index: 20;
                width: 40px;
                height: 40px;
                border-radius: 50%;
                background-color: #fff;
                color: #1a1a1a;
                box-shadow: 0 1px 4px rgba(0, 0, 0, 0.35);
                pointer-events: auto;
            }

            .setting-drawer {
                width: 100%;
                background: var(--drawer-bg-color);
                border-top-right-radius: 8px;
                border-top-left-radius: 8px;
                z-index: 5;
                pointer-events: all;
                position: absolute;
                bottom: var(--bottom-height);
                left: 0;
                overflow: auto;
                box-shadow: rgba(0, 0, 40, 0.08) 0px -1px 2px 0px, rgba(0, 0, 40, 0.08) 0px -6px 10px 0px;
                border-top: 1px solid rgb(236, 236, 236);
                border-bottom: 1px solid rgb(236, 236, 236);
            }

            .setting-drawer__overlay {
                height: 100%;
                width: 100%;
                background: rgba(0, 0, 0, .4);
                z-index: 4;
                position: absolute;
                top: 0;
                left: 0;
            }

            .setting-drawer__controls {
                display: flex;
                justify-content: space-between;
                padding-left: 16px;
                padding-right: 16px;
                padding-top: 8px;
                border-bottom: 1px solid var(--drawer-border-color);
            }

            h2 {
                margin:0.4rem;
                font-size: 1rem;
            }

            .setting-drawer__content {
                padding: 8px 16px;
                overflow-y: auto;
                display: flex;
                justify-content: center;
                overflow-x: hidden;
            }

            .cover-image-container {
                position: relative;
                width: 100%;
                overflow: hidden;
            }

            .cover-image {
                width: 100%;
                height: 100%;
                object-fit: cover;
                display: block;
            }

            .cover-image-logo-overlay {
                position: absolute;
                top: 50%;
                left: 50%;
                transform: translate(-50%, -50%);
                display: flex;
                justify-content: center;
                align-items: center;
                width: 80%;
                height: 80%;
                z-index: 2;
            }

            .cover-image-logo-overlay img {
                max-width: min(100%, 220px);
                max-height: min(100%, 72px);
                object-fit: contain;
                background-color: white;
                border-radius: 6px;
                padding: 5px;
                box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
            }

            .cover-image-controls {
                position: absolute;
                top: 8px;
                left: 12px;
                right: 12px;
                display: flex;
                justify-content: space-between;
            }

            .cover-image-icon {
                background: rgba(0, 0, 0, 0.4);
                color: white;
                border-radius: 50%;
                padding: 4px;
                cursor: pointer;
            }

            .drawer-icon-btn {
                background: none;
                border: none;
                padding: 0;
                margin: 0;
                cursor: pointer;
                display: inline-flex;
                align-items: center;
                justify-content: center;
                color: inherit;
            }

            .drawer-icon-btn:focus-visible {
                outline: 2px solid var(--main-primary-color, #005fcc);
                outline-offset: 2px;
                border-radius: 4px;
            }
        `;
    }
}
